import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Icon, Hairline, Swatch, RubberStamp, type SwatchKind } from "@/components/primitives";
import {
  useWeeklyFeature,
  useCastWeeklyVote,
  type FotwCandidateOpen,
  type FotwCandidateRevealed,
} from "@/hooks/useWeeklyFeature";
import { toast } from "@/lib/toast";

function isRevealed(c: FotwCandidateOpen | FotwCandidateRevealed): c is FotwCandidateRevealed {
  return "user_id" in c;
}

function formatWeekDate(iso: string): string {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * /find-of-the-week — weekly community vote on the strongest verified post.
 *
 * Reads strictly from get-weekly-candidates so the anonymity boundary
 * (no user_id, handle, avatar in open-week payload) holds at the data layer.
 * The UI cannot accidentally reveal a candidate because the data isn't there
 * to render.
 */
export default function FindOfTheWeek() {
  const nav = useNavigate();
  const { data, isLoading } = useWeeklyFeature();
  const [changeMode, setChangeMode] = useState(false);

  const week = data;
  const weekStart = week?.week_start ?? "";
  const castVote = useCastWeeklyVote(weekStart);

  const finalized = !!week?.finalized;
  const myVote = week?.my_vote ?? null;
  const candidates = week?.candidates ?? [];

  const handleVote = (scanId: string) => {
    if (castVote.isPending) return;
    castVote.mutate(scanId, {
      onSuccess: () => {
        toast.success(myVote === scanId ? "Vote unchanged." : "Vote in!");
        setChangeMode(false);
      },
      onError: (err: unknown) => {
        const msg = err instanceof Error ? err.message : "Couldn't record vote";
        toast.error(msg);
      },
    });
  };

  // Day-of-week sub-line in PT.
  const subline = (() => {
    const dow = new Date().getDay(); // 0=Sun
    if (finalized) return null;
    if (dow >= 1 && dow <= 3) return "Voting closes Sunday 11:59 pm PT";
    if (dow >= 4 && dow <= 6) return "Voting closes Sunday — pick your favorite";
    return "Last day to vote · closes 11:59 pm PT";
  })();

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100%", position: "relative" }}>
        {/* Top bar */}
        <div style={{
          padding: "12px 20px 0",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <button
            onClick={() => nav(-1)}
            aria-label="Back"
            style={{
              background: "none", border: "1px solid hsl(var(--ink))",
              padding: 6, cursor: "pointer", color: "hsl(var(--ink))",
            }}
          >
            {Icon.chev(16, "l")}
          </button>
          <span className="caps">Find of the Week</span>
          <div style={{ width: 30 }} />
        </div>

        <div style={{ padding: "16px 20px 32px" }}>
          <h1 style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontWeight: 800, fontSize: 28, lineHeight: 1.05,
            color: "hsl(var(--ink))", margin: 0,
          }}>
            {finalized ? "This week's winner" : "Pick this week's find"}
          </h1>
          {weekStart && (
            <div className="caps" style={{ color: "hsl(var(--rust))", marginTop: 6 }}>
              Week of {formatWeekDate(weekStart)}
            </div>
          )}
          {subline && (
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 14, lineHeight: 1.5,
              color: "hsl(var(--ink-2))",
              marginTop: 8,
            }}>
              {subline}
            </div>
          )}

          <Hairline style={{ margin: "16px 0 8px" }} />

          {isLoading && (
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              letterSpacing: "0.1em", color: "hsl(var(--ink-2))",
              padding: "30px 0", textAlign: "center",
            }}>
              Loading candidates…
            </div>
          )}

          {!isLoading && week?.skipped && (
            <div style={{
              padding: "20px",
              background: "hsl(var(--paper-3))",
              border: "1px dashed hsl(var(--ink) / 0.25)",
              borderRadius: 14,
              textAlign: "center",
              marginTop: 16,
            }}>
              <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 8 }}>
                Skipped week
              </div>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 14, lineHeight: 1.5, color: "hsl(var(--ink-2))",
              }}>
                Not enough verified posts qualified this week. Keep posting — fresh ballot next Monday.
              </div>
            </div>
          )}

          {!isLoading && !week?.skipped && candidates.length === 0 && (
            <div style={{
              padding: "20px",
              background: "hsl(var(--paper-3))",
              border: "1px dashed hsl(var(--ink) / 0.25)",
              borderRadius: 14,
              textAlign: "center",
              marginTop: 16,
            }}>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 14, lineHeight: 1.5, color: "hsl(var(--ink-2))",
              }}>
                No candidates yet. Check back when the new week opens.
              </div>
            </div>
          )}

          {candidates.map((c, i) => {
            const revealed = isRevealed(c);
            const photo = c.photos?.[0];
            const voted = myVote === c.scan_id;
            const winner = revealed && c.is_winner;
            const allowVote = !finalized && (!myVote || changeMode || voted);
            return (
              <div
                key={c.scan_id}
                style={{
                  marginTop: i === 0 ? 16 : 14,
                  border: winner ? "2px solid hsl(var(--rust))" : "1px solid hsl(var(--ink))",
                  background: "hsl(var(--cream))",
                  borderRadius: 14,
                  overflow: "hidden",
                }}
              >
                {winner && (
                  <div className="caps" style={{
                    padding: "8px 14px",
                    background: "hsl(var(--rust))",
                    color: "hsl(var(--cream))",
                    letterSpacing: "0.18em",
                    fontWeight: 700,
                  }}>
                    Winner
                  </div>
                )}

                {photo ? (
                  <div style={{
                    position: "relative", width: "100%", aspectRatio: "4/3",
                    background: "hsl(var(--paper-3))",
                  }}>
                    <img
                      src={photo}
                      alt={c.brand || c.category || "Candidate photo"}
                      style={{
                        position: "absolute", inset: 0,
                        width: "100%", height: "100%",
                        objectFit: "cover", objectPosition: "center",
                        display: "block",
                      }}
                    />
                    {finalized && revealed && (
                      <div style={{ position: "absolute", top: 12, right: 12 }}>
                        <RubberStamp
                          label="FIG"
                          sub={winner ? "WINNER" : "CANDIDATE"}
                          color="hsl(var(--rust))"
                          size={70}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ height: 140, background: "hsl(var(--paper-3))" }} />
                )}

                <div style={{ padding: "12px 14px 14px" }}>
                  {/* Metadata line — brand + category + era */}
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif",
                    fontWeight: 700, fontSize: 15, color: "hsl(var(--ink))",
                    lineHeight: 1.2,
                  }}>
                    {[c.brand, c.category].filter(Boolean).join(" · ") || "Verified piece"}
                  </div>
                  <div className="caps" style={{ color: "hsl(var(--rust))", marginTop: 4 }}>
                    {c.era ?? "Era undated"} · {c.verdict_label}
                  </div>

                  {/* Identity row — ONLY when finalized. */}
                  {revealed && (
                    <div style={{
                      display: "flex", alignItems: "center", gap: 8,
                      marginTop: 10,
                    }}>
                      <Swatch
                        kind={(c.avatar as SwatchKind) || "olive"}
                        style={{
                          width: 24, height: 24, borderRadius: "50%",
                          border: "1px solid hsl(var(--ink))",
                        }}
                      />
                      <span style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 11, letterSpacing: "0.08em",
                        color: "hsl(var(--ink))",
                      }}>
                        @{c.handle}
                      </span>
                      <div style={{ flex: 1 }} />
                      <button
                        type="button"
                        onClick={() => nav(`/post/${c.scan_id}`)}
                        className="btn"
                        style={{ padding: "6px 10px" }}
                      >
                        View post →
                      </button>
                    </div>
                  )}

                  {/* Vote row — only while voting is open. */}
                  {!finalized && (
                    <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
                      {voted && !changeMode ? (
                        <>
                          <span className="caps" style={{ color: "hsl(var(--rust))" }}>
                            ✓ Voted
                          </span>
                          <div style={{ flex: 1 }} />
                          <button
                            type="button"
                            onClick={() => setChangeMode(true)}
                            style={{
                              background: "transparent", border: "none", padding: 0,
                              cursor: "pointer",
                              fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                              letterSpacing: "0.12em", textTransform: "uppercase",
                              color: "hsl(var(--ink-2))",
                              textDecoration: "underline",
                            }}
                          >
                            Change vote
                          </button>
                        </>
                      ) : allowVote ? (
                        <button
                          type="button"
                          onClick={() => handleVote(c.scan_id)}
                          disabled={castVote.isPending}
                          className="btn btn-primary"
                          style={{ width: "100%", padding: "10px" }}
                        >
                          {castVote.isPending ? "…" : "Vote"}
                        </button>
                      ) : (
                        <span className="caps" style={{ color: "hsl(var(--ink-2))" }}>
                          Tap "Change vote" on your current pick
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </MobileFrame>
  );
}
